import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  IconButton,
  Button,
  InputBase,
  Menu,
  MenuItem,
  Link,
  Drawer,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search as SearchIcon,
  KeyboardArrowDown,
  NavigateBefore,
  NavigateNext,
  ShoppingCart,
  Menu as MenuIcon,
  Close,
  School as SchoolIcon,
  Security as SecurityIcon,
  Public as PublicIcon,
  RocketLaunch as RocketLaunchIcon,
  CalendarToday as CalendarTodayIcon,
  MailOutline as MailOutlineIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import LogoImage from '../assets/logo0.png';
import pricinghero from '../assets/larvarris_blog.png';
import background999 from '../assets/Background999.jpg';

// Styled Components
const NavButton = styled(Button)({
  color: '#fff',
  textTransform: 'none',
  fontSize: '14px',
  padding: '8px 16px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  borderRadius: 0,
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  }
});

const CategoryChip = styled(Chip)({
  backgroundColor: '#00ffd5',
  color: '#000',
  fontWeight: 500,
  fontSize: '12px',
  height: '24px',
  '&:hover': {
    backgroundColor: '#00e6c0',
  }
});

const SearchBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#f5f5f5',
  borderRadius: '4px',
  padding: '4px 12px',
});

// Add new styled components
const FeaturedTitle = styled(Typography)({
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  fontSize: '42px',
  fontWeight: 800,
  lineHeight: 1.2,
  letterSpacing: '-0.02em',
  marginBottom: '24px',
  color: '#000'
});

const FeaturedChip = styled(Chip)({
  backgroundColor: '#00ffd5',
  color: '#000',
  fontWeight: 700,
  fontSize: '13px',
  height: '28px',
  borderRadius: '2px',
  textTransform: 'uppercase',
  marginBottom: '24px',
  '&:hover': {
    backgroundColor: '#00e6c0',
  }
});

const LearnMoreButton = styled(Button)({
  backgroundColor: '#000',
  color: '#fff',
  padding: '12px 24px',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '0',
  marginTop: '32px',
  '&:hover': {
    backgroundColor: '#333',
  }
});

const ResourcesText = styled(Typography)({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '16px',
  color: '#fff',
  marginRight: '24px'
});

// Add mobile menu components
const MobileMenuButton = styled(IconButton)({
  color: '#fff',
  display: 'none',
  '@media (max-width: 900px)': {
    display: 'flex',
  }
});

// Add new styled components
const NetworkCard = styled(Box)({
  padding: '24px',
  backgroundColor: '#fff',
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  }
});

const NetworkLink = styled(Link)({
  color: '#666',
  fontSize: '14px',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
  '&:hover': {
    color: '#000'
  }
});

const NewsletterCard = styled(Box)({
  backgroundColor: '#fff',
  padding: '24px',
  borderRadius: '4px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  }
});

// Add new styled components for blog post
const BlogPostTitle = styled(Typography)({
  fontFamily: 'Roboto Slab, serif',
  fontSize: '48px',
  fontWeight: 700,
  color: '#242729',
  lineHeight: 1.2,
  marginBottom: '24px'
});

const BlogPostDate = styled(Typography)({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  color: '#6A737C',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '32px'
});

const BlogPostContent = styled(Typography)({
  fontFamily: 'Inter, sans-serif',
  fontSize: '17px',
  lineHeight: 1.6,
  color: '#242729',
  marginBottom: '24px'
});

// Mock data structure for future backend integration
const blogPosts = [
  {
    id: 1,
    category: 'COMMERCE',
    date: 'February 15, 2025',
    readTime: '40 min read',
    title: 'How to start a dropshipping business? A guide for beginners in the Middle East',
    image: 'https://your-image-url.com/blog1.jpg',
    excerpt: 'Comprehensive guide to starting your dropshipping business...'
  },
  // Add more mock posts here
];

const Blog = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 3;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        backgroundColor: '#000',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <Container maxWidth="xl">
          {/* Top Navigation */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            py: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Link href="/">
                <img 
                  src={LogoImage} 
                  alt="Larvarris AI Logo" 
                  style={{ 
                    height: '34px',
                    filter: 'brightness(0) invert(1)' // Make logo white
                  }}
                />
              </Link>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              '@media (max-width: 900px)': {
                display: 'none'
              }
            }}>
              <NavButton>Contact Us</NavButton>
              <NavButton>Blog</NavButton>
              <NavButton>Help</NavButton>
              <Button 
                variant="contained"
                sx={{ 
                  backgroundColor: '#FFE302',
                  color: '#000',
                  fontWeight: 700,
                  borderRadius: 0, // Remove border radius
                  '&:hover': {
                    backgroundColor: '#FFD700'
                  }
                }}
              >
                SIGN IN
              </Button>
              <IconButton sx={{ color: '#fff' }}>
                <ShoppingCart />
              </IconButton>
            </Box>

            {/* Mobile Menu Button */}
            <MobileMenuButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{ '@media (min-width: 901px)': { display: 'none' } }}
            >
              <MenuIcon />
            </MobileMenuButton>
          </Box>

          {/* Categories Navigation - Desktop */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3,
            py: 2,
            '@media (max-width: 900px)': {
              display: 'none'
            }
          }}>
            <ResourcesText>
              Resources Library
            </ResourcesText>
            
            <NavButton 
              endIcon={<KeyboardArrowDown sx={{ color: '#fff' }} />}
              onClick={handleMenuClick}
            >
              Skills
            </NavButton>
            <NavButton 
              endIcon={<KeyboardArrowDown sx={{ color: '#fff' }} />}
            >
              Mindset
            </NavButton>
            <NavButton>Advocacy</NavButton>
            <NavButton>Trending</NavButton>
            <NavButton>Articles</NavButton>

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '4px',
              padding: '4px 12px'
            }}>
              <InputBase
                placeholder="Search..."
                sx={{ 
                  ml: 1, 
                  flex: 1, 
                  color: '#fff',
                  '&::placeholder': {
                    color: 'rgba(255,255,255,0.7)',
                    opacity: 1
                  }
                }}
              />
              <IconButton type="submit" sx={{ p: '10px', color: '#fff' }}>
                <SearchIcon />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '80%',
            maxWidth: '300px',
            backgroundColor: '#000',
            color: '#fff'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton 
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: '#fff' }}
            >
              <Close />
            </IconButton>
          </Box>
          <Stack spacing={2}>
            <NavButton fullWidth>Contact Us</NavButton>
            <NavButton fullWidth>Blog</NavButton>
            <NavButton fullWidth>Help</NavButton>
            <NavButton fullWidth>Skills</NavButton>
            <NavButton fullWidth>Mindset</NavButton>
            <NavButton fullWidth>Advocacy</NavButton>
            <NavButton fullWidth>Trending</NavButton>
            <NavButton fullWidth>Articles</NavButton>
            <Button 
              variant="contained"
              fullWidth
              sx={{ 
                backgroundColor: '#FFE302',
                color: '#000',
                fontWeight: 700,
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: '#FFD700'
                }
              }}
            >
              SIGN IN
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 6 }}>
        {/* Hero Section */}
        <Box sx={{ 
          backgroundColor: '#f8f9fa',
          py: 4,
          mb: 4
        }}>
          <Container maxWidth="xl">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ pr: { md: 6 } }}>
                  <Typography 
                    variant="h2"
                    sx={{
                      fontSize: { xs: '32px', md: '42px' },
                      fontWeight: 800,
                      mb: 2,
                      fontFamily: 'Inter, sans-serif',
                      color: '#1a1a1a',
                      lineHeight: 1.2
                    }}
                  >
                    Discover the Future of Technology
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '18px',
                      color: '#666',
                      mb: 3,
                      lineHeight: 1.6
                    }}
                  >
                    Stay ahead with our curated content on AI innovations, tech insights, 
                    and industry trends. Join our community of tech enthusiasts and learn 
                    from expert perspectives on the latest developments in technology.
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#000',
                        color: '#fff',
                        py: 1.5,
                        px: 4,
                        borderRadius: 0,
                        '&:hover': {
                          backgroundColor: '#333'
                        }
                      }}
                    >
                      Explore Articles
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: '#000',
                        color: '#000',
                        py: 1.5,
                        px: 4,
                        borderRadius: 0,
                        '&:hover': {
                          borderColor: '#333',
                          backgroundColor: 'rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      Subscribe Now
                    </Button>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      width: '100%',
                      height: '100%',
                      border: '2px solid #000',
                      zIndex: 0
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={pricinghero}
                    alt="Larvarris Blog"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      position: 'relative',
                      zIndex: 1,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Featured Story Section */}
        <Box sx={{ mb: 8, mt: 4 }}>
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  width: '4px', 
                  height: '24px', 
                  backgroundColor: '#000', 
                  mr: 2 
                }} 
              />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: '24px', md: '28px' },
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                Featured Story
              </Typography>
            </Box>
            
            <Grid container spacing={4}>
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: '300px', md: '460px' },
                    overflow: 'hidden',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.01)'
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={background999}
                    alt="Featured Story"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="FEATURED" 
                      sx={{ 
                        backgroundColor: '#000',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '12px',
                        borderRadius: '4px',
                        height: '28px',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    />
                  </Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '28px', md: '36px' },
                      lineHeight: 1.2,
                      mb: 3,
                      fontFamily: 'Inter, sans-serif',
                      color: '#1a1a1a'
                    }}
                  >
                    The Future of AI: Transforming Business Solutions
                  </Typography>
                  <Typography 
                    sx={{ 
                      color: '#666',
                      fontSize: '16px',
                      lineHeight: 1.6,
                      mb: 3,
                      fontFamily: 'Inter, sans-serif'
                    }}
                  >
                    Discover how artificial intelligence is revolutionizing business operations 
                    and creating new opportunities for growth and innovation. Learn about the 
                    latest trends and implementations that are shaping the future of industry.
                  </Typography>
                  <Box sx={{ mt: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Typography 
                        sx={{ 
                          fontSize: '14px',
                          color: '#666',
                          mr: 3,
                          display: 'flex',
                          alignItems: 'center',
                          fontFamily: 'Inter, sans-serif'
                        }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: 16, mr: 1 }} />
                        February 20, 2024
                      </Typography>
                      <Typography 
                        sx={{ 
                          fontSize: '14px',
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          fontFamily: 'Inter, sans-serif'
                        }}
                      >
                        <AccessTimeIcon sx={{ fontSize: 16, mr: 1 }} />
                        8 min read
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#000',
                        color: '#fff',
                        py: 1.5,
                        px: 4,
                        borderRadius: 0,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontFamily: 'Inter, sans-serif',
                        '&:hover': {
                          backgroundColor: '#333'
                        }
                      }}
                    >
                      Read Full Story
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* From The Network Section */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4
          }}>
            <MenuIcon sx={{ color: '#666' }} />
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                color: '#666'
              }}
            >
              FROM THE NETWORK
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <NetworkCard>
                <NetworkLink href="https://matheducators.stackexchange.com">
                  <SchoolIcon sx={{ fontSize: 18 }} />
                  matheducators.stackexchange.com
                </NetworkLink>
                <Link
                  href="#"
                  sx={{
                    textDecoration: 'none',
                    color: '#000',
                    flex: 1
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '18px',
                      mb: 2,
                      lineHeight: 1.4
                    }}
                  >
                    Students who use AI to do their homework assignments?
                  </Typography>
                  <Typography
                    sx={{
                      color: '#666',
                      fontSize: '14px',
                      lineHeight: 1.6
                    }}
                  >
                    Perhaps the problem is not AI but homework.
                  </Typography>
                </Link>
              </NetworkCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <NetworkCard>
                <NetworkLink href="https://security.stackexchange.com">
                  <SecurityIcon sx={{ fontSize: 18 }} />
                  security.stackexchange.com
                </NetworkLink>
                <Link
                  href="#"
                  sx={{
                    textDecoration: 'none',
                    color: '#000',
                    flex: 1
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '18px',
                      mb: 2,
                      lineHeight: 1.4
                    }}
                  >
                    How can I ensure that my Git projects have not been tampered with?
                  </Typography>
                  <Typography
                    sx={{
                      color: '#666',
                      fontSize: '14px',
                      lineHeight: 1.6
                    }}
                  >
                    The fine line between paranoia and thorough security professional.
                  </Typography>
                </Link>
              </NetworkCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <NetworkCard>
                <NetworkLink href="https://worldbuilding.stackexchange.com">
                  <PublicIcon sx={{ fontSize: 18 }} />
                  worldbuilding.stackexchange.com
                </NetworkLink>
                <Link
                  href="#"
                  sx={{
                    textDecoration: 'none',
                    color: '#000',
                    flex: 1
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '18px',
                      mb: 2,
                      lineHeight: 1.4
                    }}
                  >
                    Theoretically, in an apocalypse scenario what is the best way to amputate an infected arm?
                  </Typography>
                  <Typography
                    sx={{
                      color: '#666',
                      fontSize: '14px',
                      lineHeight: 1.6
                    }}
                  >
                    If possible, stick your arm into the beam of an ion thruster.
                  </Typography>
                </Link>
              </NetworkCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <NetworkCard>
                <NetworkLink href="https://space.stackexchange.com">
                  <RocketLaunchIcon sx={{ fontSize: 18 }} />
                  space.stackexchange.com
                </NetworkLink>
                <Link
                  href="#"
                  sx={{
                    textDecoration: 'none',
                    color: '#000',
                    flex: 1
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '18px',
                      mb: 2,
                      lineHeight: 1.4
                    }}
                  >
                    What would happen if you stuck your hand into the beam of an ion thruster?
                  </Typography>
                  <Typography
                    sx={{
                      color: '#666',
                      fontSize: '14px',
                      lineHeight: 1.6
                    }}
                  >
                    Somebody on the FX team for a sci-fi movie doing their homework over here.
                  </Typography>
                </Link>
              </NetworkCard>
            </Grid>
          </Grid>
        </Box>

        {/* Newsletter Updates Section */}
        <Box sx={{ 
          bgcolor: '#1E2937', 
          py: 8, 
          px: 2,
          mt: 8 
        }}>
          <Container maxWidth="xl">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ color: '#fff', mb: { xs: 4, md: 0 } }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: '32px', md: '48px' },
                      fontWeight: 800,
                      mb: 3,
                      fontFamily: 'Inter, sans-serif'
                    }}
                  >
                    Want updates to your inbox?
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '18px',
                      mb: 4,
                      opacity: 0.9,
                      lineHeight: 1.6
                    }}
                  >
                    Every week we'll share a collection of AI innovations, tech insights, 
                    and industry updates to keep you at the forefront of technological advancement.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: '#2563EB',
                        color: '#fff',
                        borderRadius: 0,
                        py: 1.5,
                        px: 4,
                        '&:hover': {
                          bgcolor: '#1D4ED8'
                        }
                      }}
                    >
                      Previous issues
                    </Button>
                    <Button
                      startIcon={<MailOutlineIcon />}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.1)',
                        color: '#fff',
                        borderRadius: 0,
                        py: 1.5,
                        px: 4,
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.2)'
                        }
                      }}
                    >
                      Subscribe to Newsletter
                    </Button>
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container spacing={3}>
                  {[
                    {
                      date: 'FEBRUARY 19, 2025',
                      issue: 'Issue 265:',
                      title: 'AI Revolution in Healthcare',
                      description: 'This week: Breakthrough in medical diagnostics, automated patient care systems, and how AI is transforming emergency response.'
                    },
                    {
                      date: 'FEBRUARY 12, 2025',
                      issue: 'Issue 264:',
                      title: 'Smart Cities of Tomorrow',
                      description: 'This week: Implementing IoT infrastructure, sustainable energy grids, and AI-powered traffic management systems.'
                    },
                    {
                      date: 'FEBRUARY 5, 2025',
                      issue: 'Issue 263:',
                      title: 'Future of Remote Work',
                      description: 'This week: Virtual collaboration tools, secure cloud infrastructure, and maintaining team culture in digital workspaces.'
                    }
                  ].map((item, index) => (
                    <Grid item xs={12} key={index}>
                      <NewsletterCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: '#666' }} />
                          <Typography sx={{ fontSize: '14px', color: '#666' }}>
                            {item.date}
                          </Typography>
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: '#2563EB',
                            display: 'inline',
                            mr: 1
                          }}
                        >
                          {item.issue}
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: '#000',
                            display: 'inline'
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            mt: 2,
                            color: '#666',
                            fontSize: '16px',
                            lineHeight: 1.6
                          }}
                        >
                          {item.description}
                        </Typography>
                      </NewsletterCard>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Previous blog posts grid */}
        <Grid container spacing={3}>
          {/* Blog Post Card */}
          <Grid item xs={12}>
            <Box sx={{ 
              position: 'relative',
              height: '500px',
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: '#f5f5f5'
            }}>
              <Box sx={{ 
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                p: 4,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                color: '#fff'
              }}>
                <CategoryChip label="COMMERCE" />
                <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 1 }}>
                  <Typography variant="body2">February 15, 2025</Typography>
                  <Typography variant="body2">40 min read</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  How to start a dropshipping business? A guide for beginners in the Middle East
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Pagination */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          mt: 4
        }}>
          <IconButton 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
          >
            <NavigateBefore />
          </IconButton>
          <Typography>
            {currentPage}/{totalPages}
          </Typography>
          <IconButton 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
          >
            <NavigateNext />
          </IconButton>
        </Box>
      </Container>

      {/* Skills Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>Web Development</MenuItem>
        <MenuItem onClick={handleMenuClose}>Mobile Development</MenuItem>
        <MenuItem onClick={handleMenuClose}>Cloud Computing</MenuItem>
        <MenuItem onClick={handleMenuClose}>DevOps</MenuItem>
      </Menu>
    </Box>
  );
};

export default Blog;
